/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    ConfigurationParameters,
    Configuration,
    StationPoliciesApi,
    StationPolicy,
    StationPoliciesGetRequest,
} from 'client-code/channels-service';
import { TServicesParams } from '../types/TServicesParams';
import { Storage } from '../storage/Storage';
import { IStationPolicyServiceRepository } from '../interfaces/IStationPolicyServiceRepository';
import { Constants } from '../constants';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { Log } from '../logging/Log';
import { CommonHeaders } from '../CommonHeaders';
import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { DeviceModel } from '../device/DeviceModel';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class StationPolicyServiceRepositoryImpl implements IStationPolicyServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'StationPolicyServiceRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getStationPoliciesFromService(): Promise<Array<StationPolicy>> {
        try {
            const snapshotVersion = Storage.getInstance().getItem(
                Storage.Keys.STATION_POLICY_SNAPSHOT_VERSION,
            );

            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;

            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: StationPoliciesGetRequest = {
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                bodyId: this.mParams.bodyId,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                snapshotVersion,
            };

            const configuration = new Configuration(configurationParameters);
            const stationPolicyService = new StationPoliciesApi(configuration);
            const stationPolicyServiceResponse = await stationPolicyService.stationPoliciesGet(
                requestParameters,
            );
            if (stationPolicyServiceResponse.data.stationPolicies) {
                Storage.getInstance().setItem(
                    Storage.Keys.STATION_POLICIES,
                    stationPolicyServiceResponse.data.stationPolicies,
                );
                Storage.getInstance().setItem(
                    Storage.Keys.STATION_POLICY_SNAPSHOT_VERSION,
                    stationPolicyServiceResponse.data.snapshotVersion,
                );
                Storage.getInstance().setItem(
                    Storage.Keys.STATION_POLICY_SERVICE_LAST_CALL_TIME,
                    Date.now(),
                );
            }
            const stationPolicyData = Storage.getInstance().getItem(Storage.Keys.STATION_POLICIES);
            return stationPolicyData;
        } catch (error) {
            Log.e(this.TAG, 'getStationPolicies', error);
            throw error;
        }
    }

    /**
     * function to fetch station policies
     * @returns { Array<StationPolicy> } Array of Station Policy
     */
    async fetchStationPolicies(): Promise<Array<StationPolicy>> {
        try {
            const currentTime = Date.now();
            const stationPolicyServiceLastCallTime = Storage.getInstance().getItem(
                Storage.Keys.STATION_POLICY_SERVICE_LAST_CALL_TIME,
            );
            const stationPolicyData = Storage.getInstance(
                CachingPolicy.StationPolicyService.storageType,
            ).getItem(Storage.Keys.STATION_POLICIES);

            const isServiceCallRequired: boolean =
                !stationPolicyServiceLastCallTime ||
                stationPolicyServiceLastCallTime +
                    CachingPolicy.StationPolicyService.cachingDuration <
                    currentTime;
            const isDataAvailable: boolean = stationPolicyData;

            if (isServiceCallRequired || !isDataAvailable) {
                await this.getStationPoliciesFromService();
            }

            return Storage.getInstance().getItem(Storage.Keys.STATION_POLICIES);
        } catch (error) {
            Log.e(this.TAG, 'fetchStationPolicies', error);
            throw error;
        }
    }
}
